import { useState, useEffect, useRef } from 'react';
import api from '../../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { Progress } from '../ui/progress';
import { Clapperboard } from 'lucide-react';

const HEARTBEAT_SEC = 30;

// `video` = plan entry ({video_id, title, duration_minutes, watch_pct}) or
// `{ intro: true, playback, title }` for the SoulAI intro video.
export const VideoPlayerDialog = ({ video, open, onClose, onProgress }) => {
  const [playback, setPlayback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pct, setPct] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!open || !video) return;
    setPct(video.watch_pct || 0);
    if (video.intro) { setPlayback(video.playback || { ready: false }); return; }
    setLoading(true);
    setPlayback(null);
    api.get(`/api/prenatal-coach/videos/${video.video_id || video.id}/playback`)
      .then((r) => setPlayback(r.data))
      .catch((err) => setPlayback({ ready: false, message: err.response?.data?.detail || 'Could not load this video right now.' }))
      .finally(() => setLoading(false));
  }, [open, video]);

  // Watch heartbeat: while the player is open, credit 30s every 30s (server caps at duration).
  useEffect(() => {
    // V64 — a class recommended inside SoulAI chat isn't a plan slot, so there's no progress to credit.
    if (!open || !video || video.intro || video.chat_practice || !playback?.ready) return undefined;
    const durationSec = (video.duration_minutes || 20) * 60;
    timer.current = setInterval(async () => {
      try {
        const r = await api.post('/api/prenatal-coach/videos/progress', { video_id: video.video_id, watched_sec: HEARTBEAT_SEC, duration_sec: durationSec });
        setPct(r.data.watch_pct);
        onProgress?.(video.video_id, r.data);
      } catch { /* ignore heartbeat errors */ }
    }, HEARTBEAT_SEC * 1000);
    return () => clearInterval(timer.current);
  }, [open, video, playback, onProgress]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.(pct)}>
      <DialogContent className="max-w-3xl" data-testid="coach-video-player-dialog">
        <DialogHeader className="pr-8">
          <DialogTitle className="font-heading">{video?.title}</DialogTitle>
          <DialogDescription className="sr-only">SoulAI coach video player</DialogDescription>
        </DialogHeader>
        {loading && <Skeleton className="w-full aspect-video rounded-xl" />}
        {!loading && playback?.ready && (
          <div className="w-full aspect-video rounded-xl overflow-hidden bg-black relative">
            <iframe src={playback.embed_url} title={video?.title} className="w-full h-full border-0"
              allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
          </div>
        )}
        {!loading && playback?.ready && !video?.intro && !video?.chat_practice && (
          <div className="space-y-1" data-testid="coach-video-progress">
            <div className="flex justify-between text-[11px] text-muted-foreground">
              <span>Watched</span><span data-testid="coach-video-progress-pct">{pct}%{pct >= 90 ? ' · you can rate this class now' : ' · rating opens at 90%'}</span>
            </div>
            <Progress value={pct} className="h-1.5" />
          </div>
        )}
        {!loading && playback && !playback.ready && (
          <div className="flex flex-col items-center py-10 text-center" data-testid="coach-video-not-ready">
            <Clapperboard className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground max-w-sm">{playback.message || 'This video is being prepared — check back soon!'}</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
